import { createElement, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { actions } from 'store/session';
import { actions as neuroActions } from 'store/neuroDocuments';
import getCurrentJWT, { isJWTValid } from 'lib/jwtHandling';
import { getLatestRankedDiagnosis } from 'lib/diagnosis';
import { shouldAllowUnauthorized } from 'lib/authorizedRoutes';
import NoDiagnosisDialog from 'components/NoDiagnosisDialog';
import doLogout from 'lib/doLogout';
import { getLatestFinishedCommitData } from 'lib/documentHandling';

const SessionLoader = ({ children, dispatch, isLoading, isNeuroLoading, diagnoses, id, location, role }) => {
  const hasValidJWT = isJWTValid();
  if (!hasValidJWT && !shouldAllowUnauthorized(location.pathname)) {
    if (!role || role === 'PATIENT') {
      window.location.replace(LOGIN_URL);
    }

    if (role === 'DOCTOR') {
      doLogout();
    }
  }

  const [loadWaitDone, setLoadWaitDone] = useState(false);

  useEffect(
    () => {
      if (hasValidJWT && !id && !shouldAllowUnauthorized(location.pathname)) {
        // Set small timeout for initializing data loading, so that the NoDiagnosisDialog is not shown before we had a change to fetch data
        setTimeout(() => {
          setLoadWaitDone(true);
        }, 1000);

        dispatch(actions.setSessionData(getCurrentJWT()));
        dispatch(actions.loadSettings());
        dispatch(neuroActions.loadNeuroDocuments());
      }
    },
    [hasValidJWT],
  );

  if ((!isLoading && !isNeuroLoading && id) || shouldAllowUnauthorized(location.pathname)) {
    // We set the global role tag here, kinda anti-pattern (should be kept inside Redux), but will do for now
    window.__ROLE__ = role || 'PATIENT';
    if (!getLatestRankedDiagnosis(diagnoses) && !shouldAllowUnauthorized(location.pathname)) {
      return createElement(NoDiagnosisDialog, { isOpen: true, onExit: doLogout });
    }

    return createElement('div', {}, children);
  }

  // Display no diagnosis dialog if someone logs in using an informant only account
  if (!isLoading && !isNeuroLoading && !id && loadWaitDone) {
    return createElement(NoDiagnosisDialog, { isOpen: true, onExit: doLogout });
  }

  return null;
};

SessionLoader.propTypes = {
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isNeuroLoading: PropTypes.bool,
  settingsError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  location: PropTypes.object,
  role: PropTypes.string,
  diagnoses: PropTypes.array,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  id: PropTypes.string,
  challengeT: PropTypes.string,
};

const mapStateToProps = state => ({
  ...state.session,
  neuroLoading: state.neuroDocuments.isLoading,
  diagnoses: state.neuroDocuments.documents
    .filter(doc => doc.documentType === 'diagnosis')
    .map(doc => getLatestFinishedCommitData(doc)),
});

export default withRouter(
  connect(
    mapStateToProps,
    null,
  )(SessionLoader),
);
